

import { useState, useEffect, useCallback } from "react";
import { invoiceSettingsApi, quotationSettingsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { FileText, RefreshCw, AlertTriangle, Check, Wand2 } from "lucide-react";

// Rich Text Editor Toolbar Component
interface ToolbarProps {
  onFormat: (command: string, value?: string) => void;
}

function RichTextToolbar({ onFormat }: ToolbarProps) {
  return (
    <div className="flex flex-wrap gap-1 p-2 bg-stone-50 border-b border-stone-200 rounded-t-lg">
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onFormat("bold")}
          className="px-2 py-1 text-sm font-bold text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => onFormat("italic")}
          className="px-2 py-1 text-sm italic text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => onFormat("underline")}
          className="px-2 py-1 text-sm underline text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Underline"
        >
          U
        </button>
      </div>
      <div className="w-px h-6 bg-stone-300 mx-1" />
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onFormat("insertUnorderedList")}
          className="px-2 py-1 text-sm text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => onFormat("insertOrderedList")}
          className="px-2 py-1 text-sm text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Numbered List"
        >
          1. List
        </button>
      </div>
      <div className="w-px h-6 bg-stone-300 mx-1" />
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onFormat("justifyLeft")}
          className="px-2 py-1 text-sm text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Align Left"
        >
          ←
        </button>
        <button
          type="button"
          onClick={() => onFormat("justifyCenter")}
          className="px-2 py-1 text-sm text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Align Center"
        >
          ↔
        </button>
        <button
          type="button"
          onClick={() => onFormat("justifyRight")}
          className="px-2 py-1 text-sm text-stone-600 hover:bg-stone-200 rounded transition-colors"
          title="Align Right"
        >
          →
        </button>
      </div>
    </div>
  );
}

// Rich Text Editor Component
interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
}

function RichTextEditor({ value, onChange, placeholder, disabled, rows = 6 }: RichTextEditorProps) {
  const editorRef = useCallback((node: HTMLDivElement | null) => {
    if (node && node.innerHTML !== value) {
      node.innerHTML = value;
    }
  }, [value]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const content = e.currentTarget.innerHTML;
    onChange(content);
  };

  const handleFormat = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    // Update the value after formatting
    const editor = document.getElementById("rich-editor") as HTMLDivElement;
    if (editor) {
      onChange(editor.innerHTML);
    }
  };

  return (
    <div className={`border border-stone-200 rounded-lg overflow-hidden ${disabled ? "opacity-50" : ""}`}>
      <RichTextToolbar onFormat={handleFormat} />
      <div
        ref={editorRef}
        id="rich-editor"
        contentEditable={!disabled}
        onInput={handleInput}
        className="w-full px-3 py-2 text-sm text-stone-700 min-h-[120px] outline-none focus:bg-stone-50"
        style={{ minHeight: `${rows * 20}px` }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
      <style>{`
        [contentEditable]:empty:before {
          content: attr(data-placeholder);
          color: #a8a29e;
          font-style: italic;
        }
      `}</style>
    </div>
  );
}

interface DocumentSettings {
  id: number;
  prefix: string;
  format_pattern: string;
  current_number: number;
  number_padding: number;
  reset_yearly: boolean;
  separator: string;
  terms_and_conditions: string | null;
  footer_notes: string | null;
}

interface InvoiceSettings extends DocumentSettings {}

interface QuotationSettings extends DocumentSettings {
  validity_days: number;
}

interface DocumentSettingsSectionProps {
  readOnly?: boolean;
}

const FORMAT_TOKENS = [
  { token: "{PREFIX}", description: "Prefix (e.g., INV, QTN)" },
  { token: "{YEAR}", description: "Current year (2026)" },
  { token: "{MONTH}", description: "Current month (04)" },
  { token: "{NUMBER}", description: "Sequential number (00001)" },
];

export default function DocumentSettingsSection({ readOnly = false }: DocumentSettingsSectionProps) {
  const { showToast } = useToast();
  
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [quotationSettings, setQuotationSettings] = useState<QuotationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"invoice" | "quotation">("invoice");

  // Form states
  const [invoiceForm, setInvoiceForm] = useState({
    prefix: "INV",
    format_pattern: "{PREFIX}-{YEAR}-{NUMBER}",
    current_number: 0,
    number_padding: 5,
    reset_yearly: true,
    separator: "-",
    terms_and_conditions: "",
    footer_notes: "",
  });

  const [quotationForm, setQuotationForm] = useState({
    prefix: "QTN",
    format_pattern: "{PREFIX}-{YEAR}-{NUMBER}",
    current_number: 0,
    number_padding: 5,
    reset_yearly: true,
    separator: "-",
    terms_and_conditions: "",
    footer_notes: "",
    validity_days: 30,
  });

  const [showResetConfirm, setShowResetConfirm] = useState<"invoice" | "quotation" | null>(null);
  const [showResetAllConfirm, setShowResetAllConfirm] = useState<"invoice" | "quotation" | null>(null);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const [invoiceRes, quotationRes] = await Promise.all([
          invoiceSettingsApi.get(),
          quotationSettingsApi.get(),
        ]);

        if (invoiceRes.success) {
          setInvoiceSettings(invoiceRes.data);
          setInvoiceForm({
            prefix: invoiceRes.data.prefix,
            format_pattern: invoiceRes.data.format_pattern,
            current_number: invoiceRes.data.current_number,
            number_padding: invoiceRes.data.number_padding,
            reset_yearly: invoiceRes.data.reset_yearly,
            separator: invoiceRes.data.separator,
            terms_and_conditions: invoiceRes.data.terms_and_conditions || "",
            footer_notes: invoiceRes.data.footer_notes || "",
          });
        }

        if (quotationRes.success) {
          setQuotationSettings(quotationRes.data);
          setQuotationForm({
            prefix: quotationRes.data.prefix,
            format_pattern: quotationRes.data.format_pattern,
            current_number: quotationRes.data.current_number,
            number_padding: quotationRes.data.number_padding,
            reset_yearly: quotationRes.data.reset_yearly,
            separator: quotationRes.data.separator,
            terms_and_conditions: quotationRes.data.terms_and_conditions || "",
            footer_notes: quotationRes.data.footer_notes || "",
            validity_days: quotationRes.data.validity_days || 30,
          });
        }
      } catch (error) {
        console.error("Failed to load document settings:", error);
        showToast("Failed to load settings", "error");
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [showToast]);

  // Preview calculation
  const getPreview = (form: typeof invoiceForm, currentNum: number) => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, "0");
    const nextNumber = currentNum + 1;
    const number = String(nextNumber).padStart(form.number_padding, "0");

    return form.format_pattern
      .replace("{PREFIX}", form.prefix)
      .replace("{YEAR}", String(year))
      .replace("{MONTH}", month)
      .replace("{NUMBER}", number);
  };

  const handleSaveInvoice = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const response = await invoiceSettingsApi.update(invoiceForm);
      if (response.success) {
        setInvoiceSettings(response.data);
        showToast("Invoice settings saved successfully", "success");
      }
    } catch (error) {
      showToast("Failed to save invoice settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveQuotation = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const response = await quotationSettingsApi.update(quotationForm);
      if (response.success) {
        setQuotationSettings(response.data);
        showToast("Quotation settings saved successfully", "success");
      }
    } catch (error) {
      showToast("Failed to save quotation settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetNumber = async (type: "invoice" | "quotation") => {
    if (readOnly) return;
    
    setSaving(true);
    try {
      if (type === "invoice") {
        const response = await invoiceSettingsApi.update({ current_number: 0 });
        if (response.success) {
          setInvoiceSettings(response.data);
          setInvoiceForm(prev => ({ ...prev, current_number: 0 }));
          setShowResetConfirm(null);
          showToast("Invoice number reset successfully", "success");
        }
      } else {
        const response = await quotationSettingsApi.update({ current_number: 0 });
        if (response.success) {
          setQuotationSettings(response.data);
          setQuotationForm(prev => ({ ...prev, current_number: 0 }));
          setShowResetConfirm(null);
          showToast("Quotation number reset successfully", "success");
        }
      }
    } catch (error) {
      showToast(`Failed to reset ${type} number`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetAllNumbers = async (type: "invoice" | "quotation") => {
    if (readOnly) return;
    
    setSaving(true);
    try {
      if (type === "invoice") {
        const response = await invoiceSettingsApi.resetAllNumbers();
        if (response.success) {
          setInvoiceSettings(response.data);
          setInvoiceForm(prev => ({ ...prev, current_number: response.data.new_current_number }));
          setShowResetAllConfirm(null);
          showToast(`Updated ${response.data.updated_count} invoice numbers successfully`, "success");
        }
      } else {
        const response = await quotationSettingsApi.resetAllNumbers();
        if (response.success) {
          setQuotationSettings(response.data);
          setQuotationForm(prev => ({ ...prev, current_number: response.data.new_current_number }));
          setShowResetAllConfirm(null);
          showToast(`Updated ${response.data.updated_count} quotation numbers successfully`, "success");
        }
      }
    } catch (error) {
      showToast(`Failed to reset ${type} numbers`, "error");
    } finally {
      setSaving(false);
    }
  };

  const insertToken = (token: string, type: "invoice" | "quotation") => {
    if (readOnly) return;
    if (type === "invoice") {
      setInvoiceForm(prev => ({
        ...prev,
        format_pattern: prev.format_pattern + token,
      }));
    } else {
      setQuotationForm(prev => ({
        ...prev,
        format_pattern: prev.format_pattern + token,
      }));
    }
  };
  const defaultInvoiceTerms = `<ul>
    <li><strong>Payment Terms:</strong> 50% advance required to confirm booking, balance due 7 days before the event.</li>
    <li><strong>Cancellation Policy:</strong> Cancellations made 30+ days before event are refundable. Cancellations within 30 days forfeit 50% of payment.</li>
    <li><strong>Liability:</strong> We are not responsible for any external vendor services or client-provided items.</li>
    <li><strong>Amendments:</strong> Any changes must be requested in writing and are subject to availability and additional charges.</li>
    <li><strong>Validity:</strong> This invoice is valid for 30 days from the date of issue.</li>
  </ul>`;

  const defaultQuotationTerms = `<ul>
    <li><strong>Payment Terms:</strong> 50% advance required to confirm booking, balance due 7 days before the event.</li>
    <li><strong>Cancellation Policy:</strong> Cancellations made 30+ days before event are refundable. Cancellations within 30 days forfeit 50% of payment.</li>
    <li><strong>Liability:</strong> We are not responsible for any external vendor services or client-provided items.</li>
    <li><strong>Amendments:</strong> Any changes must be requested in writing and are subject to availability and additional charges.</li>
    <li><strong>Validity:</strong> This quotation is valid for 30 days from the date of issue.</li>
    <li><strong>Acceptance:</strong> Acceptance of this quotation constitutes agreement to all terms and conditions.</li>
  </ul>`;

  const handleGenerateDefaultTerms = (type: "invoice" | "quotation") => {
    if (readOnly) return;
    if (type === "invoice") {
      setInvoiceForm(prev => ({ ...prev, terms_and_conditions: defaultInvoiceTerms }));
    } else {
      setQuotationForm(prev => ({ ...prev, terms_and_conditions: defaultQuotationTerms }));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-stone-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-10 bg-stone-200 rounded"></div>
            <div className="h-10 bg-stone-200 rounded"></div>
            <div className="h-20 bg-stone-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-stone-800 font-serif">Document Settings</h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-stone-100 p-1 rounded-lg">
        <button
          type="button"
          onClick={() => setActiveTab("invoice")}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === "invoice"
              ? "bg-white text-amber-700 shadow-sm"
              : "text-stone-600 hover:text-stone-800"
          }`}
        >
          Invoice
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("quotation")}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === "quotation"
              ? "bg-white text-amber-700 shadow-sm"
              : "text-stone-600 hover:text-stone-800"
          }`}
        >
          Quotation
        </button>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800">
                Reset {showResetConfirm === "invoice" ? "Invoice" : "Quotation"} Number?
              </h3>
            </div>
            <p className="text-sm text-stone-600 mb-6">
              This will reset the current {showResetConfirm} number to 0. The next {showResetConfirm} will start from 1. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowResetConfirm(null)}
                className="px-4 py-2 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleResetNumber(showResetConfirm)}
                disabled={saving}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? "Resetting..." : "Reset"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset All Numbers Confirmation Modal */}
      {showResetAllConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800">
                Reset All {showResetAllConfirm === "invoice" ? "Invoice" : "Quotation"} Numbers?
              </h3>
            </div>
            <p className="text-sm text-stone-600 mb-6">
              This will regenerate ALL {showResetAllConfirm} numbers based on current settings format. 
              This action cannot be undone and may affect existing documents.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowResetAllConfirm(null)}
                className="px-4 py-2 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleResetAllNumbers(showResetAllConfirm)}
                disabled={saving}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? "Resetting..." : "Reset All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Settings */}
      {activeTab === "invoice" && (
        <div className="space-y-5">
          {/* Current Number - Editable */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-amber-800">Current Invoice Number</p>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setShowResetConfirm("invoice")}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Reset to 0
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={invoiceForm.current_number}
                onChange={(e) => !readOnly && setInvoiceForm(prev => ({ ...prev, current_number: parseInt(e.target.value) || 0 }))}
                disabled={readOnly}
                min={0}
                className="w-32 px-3 py-2 text-xl font-bold text-amber-700 bg-white border border-amber-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none disabled:opacity-50 font-mono"
              />
              <div className="flex items-center gap-2 text-xs text-amber-600">
                <Check className="w-4 h-4" />
                <span>Editable - changes next number</span>
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2">
              Last used invoice number. Edit to set the starting point for the next invoice.
            </p>
          </div>

          {/* Preview */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p className="text-sm font-medium text-emerald-800 mb-1">Next Invoice Number Preview:</p>
            <p className="text-2xl font-bold text-emerald-700 font-mono">
              {getPreview(invoiceForm, invoiceForm.current_number)}
            </p>
          </div>

          {/* Format Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Prefix */}
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1.5">
                Prefix
              </label>
              <input
                type="text"
                value={invoiceForm.prefix}
                onChange={(e) => !readOnly && setInvoiceForm(prev => ({ ...prev, prefix: e.target.value }))}
                maxLength={10}
                disabled={readOnly}
                className="w-full px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
              />
              <p className="text-xs text-stone-400 mt-1">e.g., INV, BILL, SI</p>
            </div>

            {/* Number Padding */}
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1.5">
                Number Padding (digits)
              </label>
              <select
                value={invoiceForm.number_padding}
                onChange={(e) => !readOnly && setInvoiceForm(prev => ({ ...prev, number_padding: parseInt(e.target.value) }))}
                disabled={readOnly}
                className="w-full px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
              >
                {[3, 4, 5, 6, 7, 8].map((num) => (
                  <option key={num} value={num}>
                    {num} digits (e.g., {String(1).padStart(num, "0")})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Format Pattern */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">
              Format Pattern
            </label>
            <input
              type="text"
              value={invoiceForm.format_pattern}
              onChange={(e) => !readOnly && setInvoiceForm(prev => ({ ...prev, format_pattern: e.target.value }))}
              disabled={readOnly}
              className="w-full px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50 font-mono"
            />
            {!readOnly && (
              <div className="bg-stone-50 rounded-lg p-3 mt-2">
                <p className="text-xs font-medium text-stone-600 mb-2">Available Tokens:</p>
                <div className="flex flex-wrap gap-2">
                  {FORMAT_TOKENS.map((item) => (
                    <button
                      key={item.token}
                      type="button"
                      onClick={() => insertToken(item.token, "invoice")}
                      className="px-2 py-1 bg-white border border-stone-300 rounded text-xs font-mono hover:bg-stone-100 transition-colors"
                      title={item.description}
                    >
                      {item.token}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Reset Yearly */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={invoiceForm.reset_yearly}
                onChange={(e) => !readOnly && setInvoiceForm(prev => ({ ...prev, reset_yearly: e.target.checked }))}
                disabled={readOnly}
                className="w-5 h-5 text-amber-600 border-stone-300 rounded focus:ring-amber-500 disabled:opacity-50"
              />
              <span className="text-sm font-medium text-stone-700">Reset number yearly</span>
            </label>
            <p className="text-xs text-stone-400 mt-1">
              When enabled, the sequence resets to 00001 each year
            </p>
          </div>

          {/* Terms & Conditions - Rich Text */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-stone-500">
                Default Terms & Conditions
              </label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleGenerateDefaultTerms("invoice")}
                  className="flex items-center gap-1 text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded transition-colors"
                >
                  <Wand2 className="w-3 h-3" />
                  Generate Default
                </button>
              )}
            </div>
            <RichTextEditor
              value={invoiceForm.terms_and_conditions}
              onChange={(value) => setInvoiceForm(prev => ({ ...prev, terms_and_conditions: value }))}
              placeholder="Enter default terms and conditions for invoices..."
              disabled={readOnly}
              rows={6}
            />
            <p className="text-xs text-stone-400 mt-1">
              These terms will be automatically added to all new invoices
            </p>
          </div>

          {/* Footer Notes - Rich Text */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">
              Footer Notes
            </label>
            <RichTextEditor
              value={invoiceForm.footer_notes}
              onChange={(value) => setInvoiceForm(prev => ({ ...prev, footer_notes: value }))}
              placeholder="Enter footer notes (e.g., Thank you for your business)..."
              disabled={readOnly}
              rows={3}
            />
          </div>

          {/* Save Button & Reset All */}
          {!readOnly && (
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={() => setShowResetAllConfirm("invoice")}
                disabled={saving}
                className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Reset All Numbers
              </button>
              <button
                type="button"
                onClick={handleSaveInvoice}
                disabled={saving}
                className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Invoice Settings"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Quotation Settings */}
      {activeTab === "quotation" && (
        <div className="space-y-5">
          {/* Current Number - Editable */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-blue-800">Current Quotation Number</p>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setShowResetConfirm("quotation")}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Reset to 0
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={quotationForm.current_number}
                onChange={(e) => !readOnly && setQuotationForm(prev => ({ ...prev, current_number: parseInt(e.target.value) || 0 }))}
                disabled={readOnly}
                min={0}
                className="w-32 px-3 py-2 text-xl font-bold text-blue-700 bg-white border border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none disabled:opacity-50 font-mono"
              />
              <div className="flex items-center gap-2 text-xs text-blue-600">
                <Check className="w-4 h-4" />
                <span>Editable - changes next number</span>
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Last used quotation number. Edit to set the starting point for the next quotation.
            </p>
          </div>

          {/* Preview */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p className="text-sm font-medium text-emerald-800 mb-1">Next Quotation Number Preview:</p>
            <p className="text-2xl font-bold text-emerald-700 font-mono">
              {getPreview(quotationForm, quotationForm.current_number)}
            </p>
          </div>

          {/* Format Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Prefix */}
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1.5">
                Prefix
              </label>
              <input
                type="text"
                value={quotationForm.prefix}
                onChange={(e) => !readOnly && setQuotationForm(prev => ({ ...prev, prefix: e.target.value }))}
                maxLength={10}
                disabled={readOnly}
                className="w-full px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
              />
              <p className="text-xs text-stone-400 mt-1">e.g., QTN, QUO, EST</p>
            </div>

            {/* Number Padding */}
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1.5">
                Number Padding (digits)
              </label>
              <select
                value={quotationForm.number_padding}
                onChange={(e) => !readOnly && setQuotationForm(prev => ({ ...prev, number_padding: parseInt(e.target.value) }))}
                disabled={readOnly}
                className="w-full px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
              >
                {[3, 4, 5, 6, 7, 8].map((num) => (
                  <option key={num} value={num}>
                    {num} digits (e.g., {String(1).padStart(num, "0")})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Format Pattern */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">
              Format Pattern
            </label>
            <input
              type="text"
              value={quotationForm.format_pattern}
              onChange={(e) => !readOnly && setQuotationForm(prev => ({ ...prev, format_pattern: e.target.value }))}
              disabled={readOnly}
              className="w-full px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50 font-mono"
            />
            {!readOnly && (
              <div className="bg-stone-50 rounded-lg p-3 mt-2">
                <p className="text-xs font-medium text-stone-600 mb-2">Available Tokens:</p>
                <div className="flex flex-wrap gap-2">
                  {FORMAT_TOKENS.map((item) => (
                    <button
                      key={item.token}
                      type="button"
                      onClick={() => insertToken(item.token, "quotation")}
                      className="px-2 py-1 bg-white border border-stone-300 rounded text-xs font-mono hover:bg-stone-100 transition-colors"
                      title={item.description}
                    >
                      {item.token}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Reset Yearly */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={quotationForm.reset_yearly}
                onChange={(e) => !readOnly && setQuotationForm(prev => ({ ...prev, reset_yearly: e.target.checked }))}
                disabled={readOnly}
                className="w-5 h-5 text-amber-600 border-stone-300 rounded focus:ring-amber-500 disabled:opacity-50"
              />
              <span className="text-sm font-medium text-stone-700">Reset number yearly</span>
            </label>
            <p className="text-xs text-stone-400 mt-1">
              When enabled, the sequence resets to 00001 each year
            </p>
          </div>

          {/* Validity Days */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">
              Default Validity (days)
            </label>
            <input
              type="number"
              value={quotationForm.validity_days}
              onChange={(e) => !readOnly && setQuotationForm(prev => ({ ...prev, validity_days: parseInt(e.target.value) || 30 }))}
              min={1}
              max={365}
              disabled={readOnly}
              className="w-full max-w-xs px-2.5 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none disabled:opacity-50"
            />
            <p className="text-xs text-stone-400 mt-1">
              Default number of days quotations remain valid
            </p>
          </div>

          {/* Terms & Conditions - Rich Text */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-stone-500">
                Default Terms & Conditions
              </label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleGenerateDefaultTerms("quotation")}
                  className="flex items-center gap-1 text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded transition-colors"
                >
                  <Wand2 className="w-3 h-3" />
                  Generate Default
                </button>
              )}
            </div>
            <RichTextEditor
              value={quotationForm.terms_and_conditions}
              onChange={(value) => setQuotationForm(prev => ({ ...prev, terms_and_conditions: value }))}
              placeholder="Enter default terms and conditions for quotations..."
              disabled={readOnly}
              rows={6}
            />
            <p className="text-xs text-stone-400 mt-1">
              These terms will be automatically added to all new quotations
            </p>
          </div>

          {/* Footer Notes - Rich Text */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">
              Footer Notes
            </label>
            <RichTextEditor
              value={quotationForm.footer_notes}
              onChange={(value) => setQuotationForm(prev => ({ ...prev, footer_notes: value }))}
              placeholder="Enter footer notes (e.g., Thank you for considering us)..."
              disabled={readOnly}
              rows={3}
            />
          </div>

          {/* Save Button & Reset All */}
          {!readOnly && (
            <div className="flex justify-between items-center pt-2">
              <button
                type="button"
                onClick={() => setShowResetAllConfirm("quotation")}
                disabled={saving}
                className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Reset All Numbers
              </button>
              <button
                type="button"
                onClick={handleSaveQuotation}
                disabled={saving}
                className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Quotation Settings"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
